// WAR · SOV chart geometry (plan §4.1, R83). Pure functions producing SVG
// path data — the overlay glues them into markup, the DOM renders them.
// Charts are DOM/SVG per the canvas/DOM split (ui-map-interface.md §1):
// they are tabular information, not game world. Series identity is the
// faction palette (world.ts) — color follows the entity, never a rank —
// and every series is also named in text, never color alone.

import { NEUTRAL_COLOR, factionColor, factionName, hex } from '../world';
import { warcharts as S } from '../gen/strings';
import { PRODUCTION_GAP_INTERVALS, PRODUCTION_SMOOTH_SAMPLES } from '../gen/presentation';
import { hhmm } from '../locale/clock';

export interface PieSlice {
  faction: number;   // 0 = unclaimed
  name: string;
  color: string;     // #rrggbb
  count: number;
  pct: number;       // 0..100
  path: string;      // donut wedge path data
}

/** One series of the sovereignty line chart. */
export interface SovLine {
  faction: number;
  name: string;
  color: string;
  points: string;    // SVG polyline points
  count: number;     // latest value, for the direct label
}

export function seriesColor(faction: number): string {
  // The neutral series is unclaimed water: a semantic gray, not a hue.
  return faction === 0 ? hex(NEUTRAL_COLOR) : hex(factionColor(faction));
}

function seriesName(faction: number): string {
  return faction === 0 ? S.unclaimed : factionName(faction);
}

/**
 * Donut wedges for the current distribution. Geometry only — the caller
 * strokes each wedge with the surface color for the 2px spacer. Zero-count
 * slices are dropped; a lone slice becomes a (nearly) full ring.
 *
 * Slices come back **largest holder first** (R97), so the donut and the
 * legend that repeats it both read as a ranking. Ties fall back to faction
 * id, ascending: two factions on equal cells must not swap places between
 * two regenerates of the same numbers (determinism, CLAUDE.md). Unclaimed
 * (faction 0) sorts by its count like any other series — early in a season
 * it genuinely is the largest holder, and pinning it would break the
 * "largest first" promise the ranking makes.
 *
 * Sorting reorders the wedges only: the first one still starts at 12
 * o'clock and the sweep is still clockwise. Colour stays keyed to the
 * faction (`seriesColor`), never to rank position, so a swap in the
 * standings re-orders the ring without recolouring it.
 */
export function pieSlices(counts: number[], cx = 50, cy = 50, r = 40, inner = 22): PieSlice[] {
  let total = 0;
  for (const n of counts) total += n;
  if (total <= 0) return [];
  const order: number[] = [];
  for (let faction = 0; faction < counts.length; faction++) {
    if (counts[faction] > 0) order.push(faction);
  }
  order.sort((a, b) => counts[b] - counts[a] || a - b);
  const out: PieSlice[] = [];
  let acc = 0;
  for (const faction of order) {
    const n = counts[faction];
    // A full circle collapses an SVG arc; cap just under one turn.
    const f0 = acc / total;
    const f1 = Math.min(f0 + n / total, f0 + 0.99999);
    acc += n;
    const a0 = 2 * Math.PI * f0 - Math.PI / 2;
    const a1 = 2 * Math.PI * f1 - Math.PI / 2;
    const large = f1 - f0 > 0.5 ? 1 : 0;
    const p = (radius: number, a: number) =>
      `${(cx + radius * Math.cos(a)).toFixed(2)} ${(cy + radius * Math.sin(a)).toFixed(2)}`;
    out.push({
      faction,
      name: seriesName(faction),
      color: seriesColor(faction),
      count: n,
      pct: (100 * n) / total,
      path: `M ${p(r, a0)} A ${r} ${r} 0 ${large} 1 ${p(r, a1)}`
        + ` L ${p(inner, a1)} A ${inner} ${inner} 0 ${large} 0 ${p(inner, a0)} Z`,
    });
  }
  return out;
}

/**
 * Polylines for the season series, one per series from `first` on. The
 * x-axis is the world clock from the first to the newest sample; y runs
 * 0..the largest count seen, shared by every drawn series (one axis,
 * always). WAR · SOVEREIGNTY passes `first = 1`: the unclaimed water held
 * the axis at the size of the sea and flattened every faction line at
 * zero (the game critique of 2026-10-14), so the series draws the
 * factions alone and the axis is theirs.
 *
 * Series stay in **faction-id order**, deliberately unsorted, unlike
 * `pieSlices()` above (R97). Nothing about a line chart reads as a
 * ranking: the lines overlay each other, each carries its own `<title>`,
 * and R95 removed the legend that a sort would have re-ordered. What the
 * order does control is SVG paint order — the last polyline is drawn on
 * top — so sorting by count would restack the lines every time two
 * factions swapped, making the topmost line flicker as the season runs.
 * Fixed order, fixed stacking.
 */
export function sovLines(
  samples: { tSec: number; cells: number[] }[],
  w = 320, h = 110, pad = 4, first = 0,
): { lines: SovLine[]; maxCells: number } {
  if (samples.length === 0) return { lines: [], maxCells: 0 };
  let maxCells = 1;
  for (const s of samples) for (let i = first; i < s.cells.length; i++) maxCells = Math.max(maxCells, s.cells[i]);
  const ts = samples.map((s) => s.tSec);
  const nSeries = Math.max(...samples.map((s) => s.cells.length));
  const lines: SovLine[] = [];
  for (let faction = first; faction < nSeries; faction++) {
    // A faction a sample does not list held no cell then: the wire lists
    // every faction, so this is a count and never a hole.
    const pts = plotPoints(ts, samples.map((s) => s.cells[faction] ?? 0), maxCells, w, h, pad);
    lines.push({
      faction,
      name: seriesName(faction),
      color: seriesColor(faction),
      points: pts.join(' '),
      count: samples[samples.length - 1].cells[faction] ?? 0,
    });
  }
  return { lines, maxCells };
}

/**
 * The chart point of every value on one time axis: x runs from the first to
 * the last stamp, y from 0 to `yMax`, and a lone stamp sits in the middle.
 * A null value is a hole and gives null, never a zero (R251). The sov line
 * and the income graphs share it, so both charts place a point one way.
 */
export function plotPoints(
  ts: readonly number[], values: readonly (number | null)[], yMax: number,
  w: number, h: number, pad = 4,
): (string | null)[] {
  if (ts.length === 0) return [];
  const t0 = ts[0];
  const span = Math.max(1, ts[ts.length - 1] - t0);
  const top = Math.max(yMax, 1e-9);
  return values.map((v, i) => {
    if (v === null) return null;
    const x = ts.length === 1 ? w / 2 : pad + ((ts[i] - t0) / span) * (w - 2 * pad);
    const y = h - pad - (v / top) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
}

/** One series cut at its holes: the polylines of two or more points, and
 * the lone points between two holes, which a polyline cannot draw (R251). */
export interface SeriesRuns {
  runs: string[];   // SVG polyline points, one entry per unbroken run
  dots: string[];   // "x,y" of every lone point
}

export function splitRuns(pts: readonly (string | null)[]): SeriesRuns {
  const out: SeriesRuns = { runs: [], dots: [] };
  let run: string[] = [];
  const close = () => {
    if (run.length === 1) out.dots.push(run[0]);
    else if (run.length > 1) out.runs.push(run.join(' '));
    run = [];
  };
  for (const p of pts) {
    if (p === null) close();
    else run.push(p);
  }
  close();
  return out;
}

// ---- ECONOMY · PRODUCTION: the income graphs (R1489) ----
//
// The server keeps a ring of the empire's two season counters of income
// per resource, mined and gathered. A graph is two lines: the rate per
// hour of each door, differenced from the ring and smoothed.

/** One reading of the income ring, as the wire carries it. Each list is
 * [alloy, biomass, volatiles]. */
export interface IncomeReading {
  tSec: number;
  mined: readonly number[];
  gathered: readonly number[];
}

/** The rate per hour of both doors at one sample, null for a hole. */
export interface IncomeRate {
  tSec: number;
  mined: number | null;
  gathered: number | null;
}

/**
 * Differences the cumulative counters of one resource into rates per hour.
 * The divisor is the game time between the two samples, never the nominal
 * interval, because a sample lands one tick late at times (R251). A gap
 * over `gapIntervals` sample intervals, a clock that did not advance, and
 * a counter that fell are holes: the line breaks there and draws no zero.
 */
export function incomeRates(
  samples: readonly IncomeReading[], kind: number, everySec: number,
  gapIntervals = PRODUCTION_GAP_INTERVALS,
): IncomeRate[] {
  const out: IncomeRate[] = [];
  const maxGap = gapIntervals * everySec;
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1], b = samples[i];
    const dt = b.tSec - a.tSec;
    const hole = dt <= 0 || (everySec > 0 && dt > maxGap);
    const rate = (from: readonly number[], to: readonly number[]): number | null => {
      if (hole) return null;
      const x0 = from[kind], x1 = to[kind];
      if (x0 === undefined || x1 === undefined || x1 < x0) return null;
      return ((x1 - x0) * 3600) / dt;
    };
    out.push({ tSec: b.tSec, mined: rate(a.mined, b.mined), gathered: rate(a.gathered, b.gathered) });
  }
  return out;
}

/**
 * The trailing moving average over `window` samples. It never reaches
 * across a hole: a hole stays a hole, and the first points after one take
 * the mean of the points they have.
 */
export function movingMean(
  values: readonly (number | null)[], window = PRODUCTION_SMOOTH_SAMPLES,
): (number | null)[] {
  const n = Math.max(1, Math.floor(window));
  return values.map((v, i) => {
    if (v === null) return null;
    let sum = 0, count = 0;
    for (let j = i; j > i - n && j >= 0; j--) {
      const x = values[j];
      if (x === null) break;
      sum += x;
      count++;
    }
    return sum / count;
  });
}

/** The smallest 1, 2 or 5 step of a power of ten at or over v, so the axis
 * label reads as a round figure. */
export function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5]) if (m * mag >= v) return m * mag;
  return 10 * mag;
}

/** One line of an income graph: the runs of its income, its newest
 *  smoothed value, and `quiet`, the stretches that lie at 0 between two
 *  samples of 0. A stretch at 0 draws as a thin quiet rule, so a line of
 *  "0/h" does not read as a dashed bar along the base (R2162). A run
 *  of income keeps the 0 sample on each side of it, so it rises from the
 *  base. */
export interface IncomeLine extends SeriesRuns {
  latest: number | null;
  quiet: string[];
}

/** The samples of a line that draw as income, and the ones that draw as
 *  a quiet stretch at 0 (R2162); a sample at the edge of both is in both. */
export function splitZero(vals: readonly (number | null)[]): {
  live: (number | null)[]; rest: (number | null)[];
} {
  const pos = (i: number) => { const v = vals[i]; return v !== null && v !== undefined && v !== 0; };
  const nil = (i: number) => vals[i] === 0;
  return {
    live: vals.map((v, i) => (v !== null && (pos(i) || pos(i - 1) || pos(i + 1)) ? v : null)),
    rest: vals.map((v, i) => (v === 0 && (nil(i - 1) || nil(i + 1)) ? v : null)),
  };
}

/** One income graph: two lines on ONE y axis from 0 to `yMax`, and the
 * world-clock span of the x axis. */
export interface IncomeGraph {
  mined: IncomeLine;
  gathered: IncomeLine;
  yMax: number;
  t0: number;
  t1: number;
}

/**
 * The geometry of one resource's income graph. Both lines share one axis,
 * because mined and gathered income of one resource are one quantity and
 * the reader compares them. The axis starts at 0 and ends at a round
 * figure over the larger line, so a flat line never fills the frame with
 * its own noise (R251). Returns null when the ring holds under two samples.
 */
export function productionGraph(
  samples: readonly IncomeReading[], kind: number, everySec: number,
  w: number, h: number, pad = 4, window = PRODUCTION_SMOOTH_SAMPLES,
): IncomeGraph | null {
  const rates = incomeRates(samples, kind, everySec);
  if (rates.length === 0) return null;
  const mined = movingMean(rates.map((r) => r.mined), window);
  const gathered = movingMean(rates.map((r) => r.gathered), window);
  let top = 0;
  for (const v of [...mined, ...gathered]) if (v !== null) top = Math.max(top, v);
  const yMax = niceCeil(top);
  const ts = rates.map((r) => r.tSec);
  const line = (vals: (number | null)[]): IncomeLine => {
    const { live, rest } = splitZero(vals);
    return {
      ...splitRuns(plotPoints(ts, live, yMax, w, h, pad)),
      latest: vals[vals.length - 1],
      quiet: splitRuns(plotPoints(ts, rest, yMax, w, h, pad)).runs,
    };
  };
  return { mined: line(mined), gathered: line(gathered), yMax, t0: ts[0], t1: ts[ts.length - 1] };
}

/**
 * A world-clock stamp, "d3 14:02": every deadline in the game runs on the
 * world's own clock, and the world clock opens at `originSec` (the
 * hello's `clock_origin_sec`, R2519), so the label matches the
 * resolution windows the player already reads.
 */
export function worldClock(atSec: number, originSec: number): string {
  const shifted = atSec + originSec;
  return `d${Math.floor(shifted / 86400) + 1} ${hhmm(shifted)}`;
}

/** The chip + name every series label carries (identity never color-alone).
 * `mine` names the reader's own faction: the chip wears the white outline
 * of the timelapse legend, and the title says why (R1620). */
export function chip(faction: number, mine = ''): string {
  return mine === ''
    ? `<span class="fchip" style="background:${seriesColor(faction)}"></span>`
    : `<span class="fchip me" style="background:${seriesColor(faction)}" title="${mine}"></span>`;
}

/**
 * The x of one world second on the season series: the axis of `sovLines`,
 * from the first to the newest sample. The playhead of the timelapse
 * stands there (R1620); a second outside the samples stands on the edge.
 */
export function seriesX(tSec: number, t0: number, t1: number, w = 320, pad = 4): number {
  const span = Math.max(1, t1 - t0);
  const x = pad + ((tSec - t0) / span) * (w - 2 * pad);
  return Math.min(w - pad, Math.max(pad, x));
}
